import { SxProps } from '@mui/material';
import { lightTheme } from '../../theme/index';

const { palette } = lightTheme;

const carouselStyle: SxProps = {
  content: '""',
  height: '100%',
  position: 'absolute',
  top: '0',
  minWidth: '4.875rem',
  right: '0',
  background: `linear-gradient(to right, rgba(255,0,0,0), ${palette.lightishAqua[400]} 54.94%)`
};
const styles: Record<string, SxProps> = {
  ssrMarkup: {
    '& .alice-carousel__stage': {
      overflow: 'hidden',
      display: 'flex',
      padding: 0
    },
    '& .alice-carousel__stage-item': {
      listStyle: 'none',
      width: '23rem !important'
    }
  },
  carouselContainerBox: {
    '.alice-carousel::after': {
      ...carouselStyle
    }
  },
  carouselMarginBottom: {
    marginBottom: { md: '-1.75rem' }
  },
  carouselBody: {
    '.alice-carousel .alice-carousel__next-btn': {
      display: { xs: 'none', md: 'block' }
    },
    '.alice-carousel .alice-carousel__prev-btn': {
      display: { xs: 'none', md: 'block' }
    }
  },
  arrowButtonStyles: {
    position: 'absolute',
    minWidth: { md: '4.8rem' },
    top: '0',
    height: '90%',
    zIndex: 1,
    cursor: 'pointer',
    transition: 'opacity 0.25s ease-in-out, height 0.25s ease-in-out'
  },
  isInteriorDesignPageStyle: {
    '.alice-carousel__stage-item': {
      paddingRight: '1rem',
      width: 'auto !important'
    },
    '.alice-carousel::after': {
      ...carouselStyle,
      background: `linear-gradient(270deg, #F4FAFB 54.94%, rgba(244, 250, 251, 0) 94.75%)`,
      width: '5%'
    },
    '.alice-carousel::before': {
      ...carouselStyle,
      background: `linear-gradient(91deg, #F4FAFB 54.94%, rgba(244, 250, 251, 0) 94.75%)`,
      width: '5%',
      left: '0rem',
      zIndex: 1
    }
  },
  localLifeDetailPageStyle: {
    '.alice-carousel::after': {
      ...carouselStyle,
      width: {
        xs: '0%',
        ss: '5%'
      },
      minWidth: {
        xs: '0rem',
        ss: '4.875rem'
      }
    }
  },
  dotsBox: {
    display: 'flex',
    justifyContent: 'center',
    marginY: {
      xs: '1rem',
      md: '0.5rem'
    }
  },
  localLifeDots: {
    width: {
      xs: '0.5rem',
      md: '0.75rem'
    },
    height: {
      xs: '0.5rem',
      md: '0.75rem'
    },
    borderRadius: '50%',
    marginX: 1,
    cursor: 'pointer',
    background: palette.primary.main,
    transition: 'background 1s ease-in-out' // Smooth transition effect
  },
  propertyDetailPageStyle: {
    '.alice-carousel::after': {
      md: {
        content: '""',
        height: '100%',
        position: 'absolute',
        top: '0',
        minWidth: '4.875rem',
        right: '0',
        background: `linear-gradient(to right, rgba(255,0,0,0), ${palette.lightishAqua[400]} 90.94%)`,
        width: '5%'
      }
    }
  },
  doNotShowIcon: {
    '.alice-carousel::after': {
      ...carouselStyle,
      display: 'none'
    }
  },
  backButtonFade: {
    '.alice-carousel::before': {
      ...carouselStyle,
      background: {
        md: `linear-gradient(to left, rgba(255,0,0,0), ${palette.lightishAqua[400]} 90.94%)`
      },
      width: { md: '5%' },
      left: { md: '-2rem' },
      zIndex: { md: 1 }
    }
  },
  doNotBeforeIcon: {
    '.alice-carousel::before': {
      ...carouselStyle,
      display: 'none'
    }
  },

  elementWidthControl: {
    marginBottom: '1rem',
    '& .MuiPaper-root': {
      width: '100%',
      height: '27rem'
    }
  }
};

export default styles;
interface ButtonStyleProps {
  activeIndex: number;
  itemData: JSX.Element[];
  isSmallScreen: boolean;
  isInteriorDesignPage: boolean;
  isPropertyDetailPage: boolean;
  carouselVisibleItemsCount?: number;
}

export function getCarouselStyle({
  isInteriorDesignPage,
  isTablet,
  activeIndex,
  itemData,
  isLocalLifeDetailPage,
  isPropertyDetailPage
}) {
  if (isInteriorDesignPage && isTablet) {
    let style = { ...styles.isInteriorDesignPageStyle };

    if (activeIndex === 0) {
      style = { ...style, ...styles.doNotBeforeIcon };
    }

    if (activeIndex === itemData.length - 2) {
      style = { ...style, ...styles.doNotShowIcon };
    }

    return style;
  }

  if (itemData.length > 0 && activeIndex === itemData.length - 2) {
    return { ...styles.propertyDetailPageStyle, ...styles.backButtonFade };
  }

  if (isLocalLifeDetailPage) {
    return styles.localLifeDetailPageStyle;
  }

  if (isPropertyDetailPage) {
    let style = { ...styles.propertyDetailPageStyle };

    if (activeIndex === itemData.length - 1) {
      style = { ...style, ...styles.doNotShowIcon };
    }

    if (activeIndex > 0) {
      style = { ...style, ...styles.backButtonFade };
    }

    return style;
  }

  return styles.carouselContainerBox;
}

export const prevButtonStyles = ({
  activeIndex,
  isInteriorDesignPage,
  isPropertyDetailPage,
  isSmallScreen
}: ButtonStyleProps): SxProps => {
  const visible =
    !isSmallScreen &&
    activeIndex === 0 &&
    (isPropertyDetailPage || isInteriorDesignPage)
      ? 0
      : 1;

  return {
    ...styles.arrowButtonStyles,
    left: isPropertyDetailPage ? '-3rem' : -60,
    opacity: visible,
    pointerEvents: !visible ? 'none' : 'auto'
  };
};

export const nextButtonStyles = ({
  activeIndex,
  itemData,
  isInteriorDesignPage,
  isPropertyDetailPage,
  isSmallScreen,
  carouselVisibleItemsCount
}: ButtonStyleProps): SxProps => {
  const itemsVisible = carouselVisibleItemsCount;
  const visible =
    !isSmallScreen &&
    activeIndex >= itemData.length - itemsVisible &&
    (isPropertyDetailPage || isInteriorDesignPage)
      ? 0
      : 1;

  return {
    ...styles.arrowButtonStyles,
    right: '-2rem',
    opacity: visible,
    pointerEvents: !visible ? 'none' : 'auto'
  };
};
